import { useEffect } from 'react';
import debug from 'debug';

const log = debug('rois:offline:unload-guard');

/**
 * Avisa antes de cerrar la pestaña si hay trabajo encolado sin enviar.
 *
 * ── Por qué ───────────────────────────────────────────────────────────────
 * Hoy la app deja cerrar la pestaña en silencio con actuaciones, accidentes o
 * tramos dentro de la cola offline. El operario ve el aviso rojo mientras la
 * app está abierta, pero nada le detiene al cerrar — y desde fuera no hay forma
 * de saber que quedaba trabajo dentro.
 *
 * La cola sobrevive al cierre (vive en IndexedDB), así que en el caso normal el
 * trabajo se recupera al volver a entrar. Pero "en el caso normal" hace mucho
 * trabajo en esa frase: basta con que la tablet cambie de manos, se reinstale
 * la PWA o el navegador desaloje el almacenamiento para que se pierda. Y sobre
 * todo, el operario se va a casa creyendo que ha entregado el parte.
 *
 * ── Lo que el navegador permite ───────────────────────────────────────────
 * El texto NO se puede personalizar: desde hace años todos los navegadores
 * muestran su propio mensaje genérico e ignoran cualquier cadena que se les
 * pase. Por eso esto no necesita ninguna clave i18n nueva.
 *
 * Además el aviso solo aparece si el usuario ha interactuado con la página
 * (*user activation*): un cierre inmediato tras cargar no lo dispara. Es una
 * protección del navegador contra páginas que secuestran el cierre, y en la
 * práctica no nos afecta — para tener cola pendiente hay que haber trabajado.
 *
 * El listener solo se registra cuando hay algo que perder, y se retira en
 * cuanto la cola se vacía. No queda un `beforeunload` permanente estorbando.
 *
 * Recibe el total en vez de llamar a `useOnlineStatus()` por su cuenta: quien
 * lo monta (`OfflineBanner`) ya tiene esos contadores, y duplicar el hook
 * significaría releer las cuatro colas de IndexedDB sin necesidad.
 *
 * @param pendingTotal operaciones sin enviar (reintentables + en error terminal)
 */
/**
 * La app se va de la página A PROPÓSITO (sesión terminada → login): no se
 * pregunta «¿salir de esta página?». El trabajo sigue en IndexedDB y el login
 * lo dice. Medido en tablet (2026-10-09): el operario veía el diálogo del
 * navegador sin haber hecho nada y no sabía qué contestar. No se rearma: la
 * página se descarga justo después.
 */
let leavingOnPurpose = false;

export function leavePageWithoutWarning(): void {
  leavingOnPurpose = true;
}

export function useWarnOnUnloadWithPendingWork(pendingTotal: number): void {
  const total = pendingTotal;

  useEffect(() => {
    if (total <= 0) return;

    const handler = (event: BeforeUnloadEvent) => {
      if (leavingOnPurpose) return;
      // La forma canónica: `preventDefault()` es lo que exige la especificación
      // actual; `returnValue` sigue haciendo falta para navegadores antiguos.
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    log('cola con %d operación(es) sin enviar — se activa el aviso de cierre', total);
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      log('cola vacía — aviso de cierre retirado');
    };
  }, [total]);
}
